import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { memo, useContext, useMemo } from 'react';
import _ from 'underscore';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { ElementHelper } from '@bingads-webui-cc-react/helper';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Link } from '@fluentui/react';

import { getStyle } from './get-style';
import type { CouponBannerProps } from './types';

const getFormattedText = (rawString: string) => {
  const boldTexts: string[] = [];

  const stringFormatterSections = _.map(
    rawString.split('{boldStart}'),
    (str) => {
      const splitSubstring = str.split('{boldEnd}');
      if (splitSubstring.length === 2) {
        const boldTextLength = boldTexts.length;

        boldTexts.push(splitSubstring[0]);
        return `{${boldTextLength}}${splitSubstring[1]}`;
      }
      return splitSubstring[0];
    }
  );

  const stringFormatter = stringFormatterSections.join('');

  return ElementHelper.renderTextWithBolds(stringFormatter, boldTexts);
};

const CouponBannerUnstyled = memo(({
  classes,
  showCouponAlert,
  couponInfo,
}: CouponBannerProps) => {
  const i18n = useContext(I18nContext);

  const renderDefaultCouponBanner = useMemo(() => {
    if (showCouponAlert && !couponInfo) {
      return (
        <div className="alert alert-coupon" role="alert">
          <div className="coupon-icon-svg iconba-Coupon iconba" />
          <div className="alert__content">
            <span className="alert__message">
              {getFormattedText(i18n.getString(_TL_(
                '{boldStart}You\'re eligible for free advertising as a new customer!{boldEnd} You\'ll see the details when you set up payment.',
                {
                  comment: '{boldStart} and {boldEnd} are the start/stop points for html tags to make the text bold',
                }
              )))}
            </span>
          </div>
        </div>
      );
    }
    return null;
  }, [couponInfo, i18n, showCouponAlert]);

  const renderCouponInfoBanner = useMemo(() => {
    if (showCouponAlert && couponInfo) {
      return (
        <div className="alert alert-coupon" role="alert">
          <div className={classNames(couponInfo.class, classes.signupBannerNoteStyle)}>
            <div className={classNames(classes.signupCouponIcon, 'coupon-icon-svg', 'iconba-Coupon', 'iconba')} />
            <span className={classNames(classes.signupCouponText, 'coupon-text')}>
              <ReactTemplate
                model={
                  {
                    link: (text: React.ReactNode) => (
                      <Link
                        className={classes.signupLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        href={couponInfo.href}
                      >
                        {text}
                      </Link>
                    ),
                  }
                }
                template={couponInfo.template}
              />
            </span>
          </div>
        </div>
      );
    }
    return null;
  }, [classes.signupBannerNoteStyle, classes.signupCouponIcon, classes.signupCouponText, classes.signupLink, couponInfo, showCouponAlert]);

  return (
    <div className="alert-center">
      <ol
        role="group"
        className={classNames('alert-center__level', classes.alertList)}
        data-level="Info"
      >
        {renderDefaultCouponBanner}
        {renderCouponInfoBanner}
      </ol>
    </div >
  );
});

(CouponBannerUnstyled as typeof CouponBannerUnstyled & {
  propTypes?: React.WeakValidationMap<CouponBannerProps>;
}).propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  showCouponAlert: PropTypes.bool,
  couponInfo: PropTypes.shape({
    class: PropTypes.string,
    model: PropTypes.shape({}),
    template: PropTypes.string,
    href: PropTypes.string,
  }),
} as React.WeakValidationMap<CouponBannerProps>;
CouponBannerUnstyled.displayName = 'CouponBanner';

(CouponBannerUnstyled as typeof CouponBannerUnstyled & {
  defaultProps?: Partial<CouponBannerProps>;
}).defaultProps = {
  showCouponAlert: false,
  couponInfo: null,
};

export const CouponBanner = withDefaultStyles(CouponBannerUnstyled, getStyle);
